import { CommonModule } from '@angular/common';
import { Component, Input, inject } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  ButtonDirective,
  DropdownComponent,
  DropdownItemDirective,
  DropdownMenuDirective,
  DropdownToggleDirective,
} from '@coreui/angular';
import { IconDirective } from '@coreui/icons-angular';
import { APP_LOCALES, AppLocale, LocaleService } from '../../service/locale.service';

@Component({
  selector: 'app-language-switcher',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    DropdownComponent,
    DropdownToggleDirective,
    DropdownMenuDirective,
    DropdownItemDirective,
    ButtonDirective,
    IconDirective,
  ],
  template: `
    <ng-container *ngIf="placement === 'footer'; else headerSwitcher">
      <div class="language-switcher language-switcher--footer" role="group" [attr.aria-label]="'common.language' | translate">
        <span class="language-switcher__footer-label">{{ 'common.language' | translate }}</span>
        <div class="language-switcher__footer-options">
          <button
            type="button"
            class="language-switcher__footer-btn"
            *ngFor="let opt of locales"
            [class.language-switcher__footer-btn--active]="opt.id === locale.current"
            (click)="select(opt.id)"
          >
            {{ opt.labelKey | translate }}
          </button>
        </div>
      </div>
    </ng-container>

    <ng-template #headerSwitcher>
      <c-dropdown variant="nav-item" class="language-switcher">
        <button
          type="button"
          cDropdownToggle
          class="btn btn-link text-decoration-none language-switcher__toggle px-2 py-1"
          [attr.aria-label]="'common.language' | translate"
          [attr.title]="'common.language' | translate"
        >
          <svg cIcon name="cilLanguage" size="lg"></svg>
          <span class="language-switcher__label ms-1 d-none d-md-inline">{{ currentLabelKey | translate }}</span>
        </button>
        <div cDropdownMenu class="language-switcher__menu">
          <button
            cDropdownItem
            type="button"
            class="language-switcher__item"
            *ngFor="let opt of locales"
            [class.active]="opt.id === locale.current"
            (click)="select(opt.id)"
          >
            {{ opt.labelKey | translate }}
          </button>
        </div>
      </c-dropdown>
    </ng-template>
  `,
  styles: [`
    .language-switcher__toggle {
      color: inherit;
      display: inline-flex;
      align-items: center;
    }
    .language-switcher__item.active {
      font-weight: 600;
    }
    .language-switcher--footer {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.65rem 1rem;
    }
    .language-switcher__footer-label {
      font-size: 0.85rem;
      font-weight: 600;
      color: var(--text-primary, #172016);
    }
    .language-switcher__footer-options {
      display: inline-flex;
      flex-wrap: wrap;
      gap: 0.35rem;
      padding: 0.2rem;
      border-radius: 999px;
      border: 1px solid var(--border-color, rgba(23, 32, 22, 0.18));
      background: var(--surface-base, #fff);
    }
    .language-switcher__footer-btn {
      border: 0;
      background: transparent;
      color: var(--text-secondary, rgba(23, 32, 22, 0.72));
      font-size: 0.82rem;
      font-weight: 600;
      padding: 0.35rem 0.75rem;
      border-radius: 999px;
      cursor: pointer;
      transition: background 0.15s ease, color 0.15s ease;
    }
    .language-switcher__footer-btn--active {
      background: var(--accent-soft, rgba(46, 125, 50, 0.12));
      color: var(--accent-color, #2e7d32);
    }
  `],
})
export class LanguageSwitcherComponent {
  /** header = nav dropdown; footer = inline segmented buttons */
  @Input() placement: 'header' | 'footer' = 'header';

  readonly locale = inject(LocaleService);
  readonly locales = APP_LOCALES;

  get currentLabelKey(): string {
    return this.locales.find((l) => l.id === this.locale.current)?.labelKey || 'common.language_zh_tw';
  }

  select(id: AppLocale): void {
    if (id !== this.locale.current) {
      this.locale.switchLocale(id);
    }
  }
}
